import {QueryClient} from '@tanstack/react-query';
import axios, {AxiosRequestConfig} from 'axios';
import {getActiveWorkspaceId} from '../workspace/active-workspace-id';
import {isAbsoluteUrl} from '../utils/urls/is-absolute-url';
import {errorStatusIs} from '@common/utils/http/error-status-is';
import {
  getStudioApiOrigin,
  isStudioRemoteApiUrl,
} from '@app/studio/studio-api-url';
import {
  clearStudioSession,
  ensureStudioSession,
  getStudioCsrfToken,
} from '@app/studio/studio-session';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: (failureCount, err) => {
        if (
          errorStatusIs(err, 401) ||
          errorStatusIs(err, 403) ||
          errorStatusIs(err, 404) ||
          errorStatusIs(err, 429) ||
          errorStatusIs(err, 503)
        ) {
          return false;
        }
        return failureCount < 1;
      },
    },
  },
});

export const apiClient = axios.create();
apiClient.defaults.withCredentials = true;
apiClient.defaults.responseType = 'json';
// @ts-ignore
apiClient.defaults.headers = {
  common: {
    Accept: 'application/json',
  },
};

const internalEndpoints = ['auth', 'secure', 'log-viewer', 'horizon'];
// @ts-ignore
apiClient.interceptors.request.use(async (config: AxiosRequestConfig) => {
  if (
    !internalEndpoints.some(endpoint => config.url?.startsWith(endpoint)) &&
    !isAbsoluteUrl(config?.url)
  ) {
    config.url = `api/v1/${config.url}`;
  }

  const studioOrigin = getStudioApiOrigin();
  if (studioOrigin && isStudioRemoteApiUrl(config.url)) {
    const skipSession = (config.url || '').includes('/studio/session');
    if (!skipSession) {
      await ensureStudioSession();
    }
    const path = config.url?.startsWith('/') ? config.url : `/${config.url}`;
    if (config.url && !config.url.startsWith('http')) {
      config.url = `${studioOrigin}${path}`;
    }
    config.withCredentials = true;
    const csrf = getStudioCsrfToken();
    if (csrf) {
      config.headers = {
        ...config.headers,
        'X-XSRF-TOKEN': csrf,
        'X-Requested-With': 'XMLHttpRequest',
      };
    }
  }

  const method = config.method?.toUpperCase();

  // transform array query params in GET request to comma separated string
  if (method === 'GET' && Array.isArray(config.params?.with)) {
    config.params.with = config.params.with.join(',');
  }
  if (method === 'GET' && Array.isArray(config.params?.load)) {
    config.params.load = config.params.load.join(',');
  }
  if (method === 'GET' && Array.isArray(config.params?.loadCount)) {
    config.params.loadCount = config.params.loadCount.join(',');
  }

  // add workspace query parameter
  const workspaceId = getActiveWorkspaceId();
  if (workspaceId) {
    const method = config.method?.toLowerCase();
    if (['get', 'post', 'put'].includes(method!)) {
      config.params = {...config.params, workspaceId};
    }
  }

  // override PUT, DELETE, PATCH methods, they might not be supported on the backend
  if (method === 'PUT' || method === 'DELETE' || method === 'PATCH') {
    config.headers = {
      ...config.headers,
      'X-HTTP-Method-Override': method,
    };
    config.method = 'POST';
    config.params = {
      ...config.params,
      _method: method,
    };
  }

  if (import.meta.env.SSR) {
    config.headers = {
      ...config.headers,
      referer: 'http://localhost',
    };
  }

  return config;
});

// В режиме отладки логируем все ошибки API в консоль — метод, URL, статус, тело ответа.
// Добавлено 2026-03-13 для упрощения отладки (ошибки видны в DevTools Console).
apiClient.interceptors.response.use(
  response => response,
  async err => {
    if (import.meta.env.DEV) {
      const method = err.config?.method?.toUpperCase();
      const url = err.config?.url;
      const status = err.response?.status;
      const data = err.response?.data;
      console.error('[API Error]', {method, url, status, data}, err);
    }
    const cfg = err.config as
      | (AxiosRequestConfig & {_studioRetry?: boolean})
      | undefined;
    if (
      cfg &&
      !cfg._studioRetry &&
      err.response?.status === 401 &&
      isStudioRemoteApiUrl(cfg.url) &&
      getStudioApiOrigin()
    ) {
      cfg._studioRetry = true;
      clearStudioSession();
      await ensureStudioSession();
      return apiClient.request(cfg);
    }
    return Promise.reject(err);
  },
);
